import React from "react";
import { TouchableOpacity, Pressable } from "react-native";
import VDBIcon from "@components/icons/VDBIcon";
import { Spacer, VDBText } from "@components/atoms";
import { IconsVariant } from "@components/icons/VDBIcon";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTheme } from "styled-components/native";
import styled from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import {
  ActiveSortableOptionItemProps,
  ascending,
} from "@scenes/searchResults/types";
import { VDBIcons } from "@utils/VDBIcons";

const ActiveSortingItemComponent = ({
  item,
  isActive,
  paramList,
  invertParam,
  removeFromActiveOptions,
  onDragStart,
  onDragEnd,
  activeData,
}: ActiveSortableOptionItemProps) => {
  const theme = useTheme();
  const typography = useTypographyStore();
  const toTestId = (value: string) => value.replace(/\s+/g, "-").toLowerCase();
  const itemKey = toTestId(String(item?.name ?? "unknown"));

  return (
    <SelectedOptionParentContainer
      theme={theme}
      isActive={isActive}
      mLastOption={activeData.indexOf(item) === activeData.length - 1}
      mFirstOption={activeData.indexOf(item) === 0}
    >
      <DragIconContainer
        theme={theme}
        isActive={isActive}
        onPressIn={onDragStart}
        onPressOut={onDragEnd}
        onMouseDown={onDragStart}
        onMouseUp={onDragEnd}
        onTouchStart={onDragStart}
        onTouchEnd={onDragEnd}
      >
        <VDBIcon
          name={VDBIcons.DRAG_ALT}
          size={24}
          variant={IconsVariant.VDBCustom}
          color={theme["core-05"]}
        />
      </DragIconContainer>

      <OptionInfoContainer>
        <IconTextContainer
          testID={`sorting.active.iconText.${itemKey}`}
          data-testId={`sorting.active.iconText.${itemKey}`}
        >
          <Spacer size={SpaceType.Basic} />
          <Pressable
            onPress={() => {
              invertParam(item);
            }}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            style={{
              transform: [
                {
                  rotate: paramList.includes(item.name + " " + ascending)
                    ? "180deg"
                    : "0deg",
                },
              ],
            }}
          >
            <VDBIcon
              name={VDBIcons.SORT_DESCENDING}
              size={24}
              variant={IconsVariant.VDBCustom}
              color={theme["core-05"]}
            />
          </Pressable>

          <Spacer size={SpaceType.Basic} />
          <VDBText color={theme["core-05"]} type={typography["Body-M"]}>
            {item.label}
          </VDBText>
        </IconTextContainer>
        {!isActive && (
          <RemoveIconContainer
            testID={`sorting.active.remove.${itemKey}`}
            data-testId={`sorting.active.remove.${itemKey}`}
          >
            <Pressable
              onPress={() => {
                removeFromActiveOptions(item);
              }}
            >
              <VDBIcon
                name={VDBIcons.CLOSE_CIRCLE}
                size={20}
                variant={IconsVariant.VDBCustom}
                color={theme["core-04"]}
              />
            </Pressable>
          </RemoveIconContainer>
        )}
      </OptionInfoContainer>
    </SelectedOptionParentContainer>
  );
};

const SelectedOptionParentContainer = styled.View<any>`
  height: 56px;
  flex-direction: row;
  border-width: ${({ isActive }) => (isActive ? 2 : 0)}px;
  border-bottom-width: ${({ mLastOption }) => (mLastOption ? 0 : 1)}px;
  border-color: ${({ theme }) => theme["core-03"]};
  border-top-left-radius: ${({ isActive, mFirstOption }) =>
    isActive || mFirstOption ? 8 : 0}px;
  border-top-right-radius: ${({ isActive, mFirstOption }) =>
    isActive || mFirstOption ? 8 : 0}px;
  border-bottom-left-radius: ${({ isActive, mLastOption }) =>
    isActive || mLastOption ? 8 : 0}px;
  border-bottom-right-radius: ${({ isActive, mLastOption }) =>
    isActive || mLastOption ? 8 : 0}px;
  background-color: ${({ theme }) => theme["core-01"]};
  margin-start: ${({ isActive }) => (isActive ? 5 : 0)}px;
`;

const DragIconContainer = styled(TouchableOpacity)<any>`
  height: ${({ isActive }) => (isActive ? 54 : 56)}px;
  margin-start: ${({ isActive }) => (isActive ? -2 : 0)}px;
  width: 40px;
  align-items: center;
  justify-content: center;
  border-right-width: 1px;
  border-color: ${({ theme }) => theme["core-03"]};
  background-color: ${({ theme, isActive }) =>
    isActive ? theme["extra-04"] : "#00000000"};
  border-top-left-radius: ${({ isActive }) => (isActive ? 8 : 0)}px;
  border-bottom-left-radius: ${({ isActive }) => (isActive ? 8 : 0)}px;
`;

const OptionInfoContainer = styled.View`
  height: 56px;
  flex: 1;
  justify-content: space-between;
  flex-direction: row;
`;
const IconTextContainer = styled.View`
  height: 56px;
  flex-direction: row;
  align-items: center;
  flex: 0.7;
`;

const RemoveIconContainer = styled.View`
  height: 56px;
  width: 56px;
  align-items: center;
  justify-content: center;
`;

export default ActiveSortingItemComponent;
